import { VFC, useMemo } from 'react'
import { AssetReference, ImageTransforms } from '@opallabs/cassini'
import { Asset } from '@opallabs/kuiper'
import { AssetNodeChildProps } from '../../types'
import {
  DEFAULT_IMAGE_SIZE,
  calculateImageDimensions,
  calculateResizedImageTransforms,
  textEditorAssetWrapper,
} from '../../utils'
import * as styles from './styles'

const AssetImageNode: VFC<AssetNodeChildProps> = ({
  asset,
  node,
  onDelete,
  onExpandAsset,
  readOnly = false,
  selected = false,
}) => {
  const assetV2 = asset
    ? ({
        uuid: asset.assetUuid,
        asset_type: asset.assetType ?? undefined,
      } as AssetReference)
    : null

  const dimensions: { width: number; height: number } = {
    width: node?.attrs?.width ?? DEFAULT_IMAGE_SIZE,
    height: node?.attrs?.height ?? DEFAULT_IMAGE_SIZE,
  }

  const isResizingVisible = selected && !readOnly

  // Fetch images at 500px width when resized.
  // Otherwise use calculated thumbnail dimensions when at default size.
  const transforms = useMemo<ImageTransforms | undefined>(() => {
    if (!asset.width || !asset.height) {
      return undefined
    }

    const thumbnailDimensions = calculateImageDimensions(
      asset.width,
      asset.height
    )

    const isResized =
      dimensions.width !== thumbnailDimensions.width ||
      dimensions.height !== thumbnailDimensions.height

    if (isResized) {
      return calculateResizedImageTransforms(asset.width, asset.height)
    }

    return {
      width: thumbnailDimensions.width,
      height: thumbnailDimensions.height,
    }
  }, [dimensions.width, dimensions.height, asset.width, asset.height])

  const handleExpand =
    typeof onExpandAsset === 'function' ? () => onExpandAsset(asset) : undefined

  return (
    <div
      className={textEditorAssetWrapper}
      css={styles.assetNodeViewWrapper({
        width: dimensions.width,
        height: dimensions.height,
        selected: isResizingVisible,
      })}
    >
      <Asset
        assetReference={assetV2}
        deletable={!readOnly}
        removeable={!readOnly}
        readOnly={!handleExpand && readOnly}
        hasAssetOverlay={true}
        downloadable={true}
        placeholder={false}
        transforms={transforms}
        onEmbiggen={handleExpand}
        onDelete={onDelete}
        infoOverlayStyles={styles.assetOverlay}
        overrideStyles={{
          assetBase: styles.assetBase({ readOnly }),
          asset: styles.asset,
        }}
      />
    </div>
  )
}

export default AssetImageNode
